/**
 * Экран рождения: жребий (A1) и карточка рождения.
 *
 * Исход жребия уже решён движком при `game.born()` (`life.iso3`); здесь
 * только показ: поле точек по одной на страну, каждую секунду вспыхивают
 * четыре случайные (единственное место в клиенте, где допустим
 * `Math.random` — он не влияет на игру), «Родиться» — точки гаснут волной
 * к стране рождения за WAVE_MS, потом карточка. При reduced motion жребий
 * пропускается и карточка показывается сразу.
 *
 * Порядок рождения среди детей и три подсказки имени движок не хранит —
 * они выводятся детерминированно из `life.seed`, так что карточка одной
 * жизни всегда одинакова.
 */
import { getValue, type Dataset } from '@life/data';
import { createRng, type Life } from '@life/engine';
import { keysWithPrefix, t, yearsWord } from '@life/i18n';
import { useEffect, useMemo, useState } from 'preact/hooks';
import { ThemeToggle } from '../components/ThemeToggle';
import { ordinalChild } from '../lib/format';
import { prefersReducedMotion } from '../lib/motion';
import type { Game } from '../state/game';

/** Длительность волны гашения точек. */
export const WAVE_MS = 2500;
/** Период вспышек в жребии и сколько точек вспыхивает за раз. */
export const LOTTERY_TICK_MS = 1000;
export const LIT_PER_TICK = 4;
const DOT_COLS = 16;
const NAME_SUGGESTIONS = 3;

export interface BirthScreenProps {
  game: Game;
  ds: Dataset;
  /** «Поделиться»: карточка рождения в PNG (Task 10). Без колбэка кнопка не показывается. */
  onShare?: (life: Life) => void;
}

type Phase = 'lottery' | 'wave' | 'card';

/** Пустая карта жизни: 80 пунктирных клеток. */
function EmptyStrip() {
  const cells = Array.from({ length: 80 }, (_, i) => <i key={i} class="off" />);
  return <div class="strip" aria-hidden="true">{cells}</div>;
}

/** Порядок рождения среди детей семьи, 1..siblings+1, из seed. */
export function birthOrder(life: Life): number {
  return createRng(life.seed).fork('birthOrder').int(1, life.siblings + 1);
}

/** Три имени по полу, детерминированно из seed, без повторов. */
export function suggestedNames(life: Life): string[] {
  const rng = createRng(life.seed).fork('nameSuggest');
  const pool = keysWithPrefix(`name.${life.sex}.`).map((k) => t(k));
  const out: string[] = [];
  while (out.length < NAME_SUGGESTIONS && out.length < pool.length) {
    const name = rng.pick(pool);
    if (!out.includes(name)) out.push(name);
  }
  return out;
}

function childrenLine(life: Life): string {
  return ordinalChild(birthOrder(life), life.siblings + 1, life.sex);
}

/** Задержка гашения точки: дальние от цели гаснут первыми, волна сходится к цели. */
function waveDelay(i: number, target: number, count: number): number {
  const col = (k: number) => k % DOT_COLS;
  const row = (k: number) => Math.floor(k / DOT_COLS);
  const rows = Math.ceil(count / DOT_COLS);
  const dist = Math.hypot(col(i) - col(target), row(i) - row(target));
  const maxDist = Math.hypot(DOT_COLS, rows);
  return Math.round((1 - dist / maxDist) * WAVE_MS * 0.8);
}

interface LotteryProps {
  ds: Dataset;
  life: Life;
  phase: Phase;
  onDraw: () => void;
  onSkip: () => void;
}

function Lottery({ ds, life, phase, onDraw, onSkip }: LotteryProps) {
  const count = ds.countries.length;
  const target = ds.countries.findIndex((c) => c.iso3 === life.iso3);
  const [lit, setLit] = useState<ReadonlySet<number>>(() => new Set());

  useEffect(() => {
    if (phase !== 'lottery') return;
    const id = setInterval(() => {
      const next = new Set<number>();
      // Визуальная случайность: исход жребия уже в life.iso3.
      while (next.size < Math.min(LIT_PER_TICK, count)) next.add(Math.floor(Math.random() * count));
      setLit(next);
    }, LOTTERY_TICK_MS);
    return () => clearInterval(id);
  }, [phase, count]);

  const waving = phase === 'wave';
  return (
    <section class="lottery">
      <div class="eyebrow">{t('ui.birth.lottery.eyebrow')}</div>
      <h1>{t('ui.birth.lottery.title')}</h1>
      <p class="lead">{t('ui.birth.lottery.lead')}</p>
      <div class="dots" role="img" aria-label={t('ui.birth.lottery.alt')}>
        {ds.countries.map((c, i) => {
          const isTarget = waving && i === target;
          const cls = isTarget ? 'target' : waving ? 'out' : lit.has(i) ? 'lit' : '';
          const style = waving && !isTarget ? { transitionDelay: `${waveDelay(i, target, count)}ms` } : undefined;
          return <i key={c.iso3} class={cls} data-iso3={c.iso3} style={style} />;
        })}
      </div>
      <div class="actions">
        <button type="button" class="btn primary block" disabled={waving} onClick={onDraw}>
          {t('ui.birth.draw')}
        </button>
        <button type="button" class="btn ghost block" onClick={onSkip}>
          {t('ui.birth.skip')}
        </button>
      </div>
    </section>
  );
}

interface CardProps {
  game: Game;
  ds: Dataset;
  life: Life;
  onShare?: ((life: Life) => void) | undefined;
}

function BirthCard({ game, ds, life, onShare }: CardProps) {
  const country = ds.countries.find((c) => c.iso3 === life.iso3);
  const e0 = Math.round(getValue(ds, life.iso3, life.sex === 'f' ? 'lifeExpF' : 'lifeExpM', life.birthYear).v);
  const names = useMemo(() => suggestedNames(life), [life.seed, life.sex]);

  return (
    <section>
      <div class="birth">
        <div class="eyebrow">{t('ui.birth.eyebrow')}</div>
        <h3 class="birth-name">{life.name}</h3>
        <div class="field">
          <label for="birth-name">{t('ui.birth.name')}</label>
          <input
            id="birth-name"
            type="text"
            maxLength={40}
            value={life.name}
            onInput={(e) => game.rename((e.currentTarget as HTMLInputElement).value)}
          />
          <p class="hint">{t('ui.birth.name.hint')}</p>
          <div class="names" role="group" aria-label={t('ui.birth.name.suggest')}>
            {names.map((n) => (
              <button type="button" class="btn" key={n} onClick={() => game.rename(n)}>
                {n}
              </button>
            ))}
          </div>
        </div>
        <div class="meta">
          <b>{t('ui.birth.place', { country: country?.nameRu ?? life.iso3, year: life.birthYear })}</b>
          <br />
          {t(`ui.birth.sex.${life.sex}`)} · {t(life.urban ? 'ui.birth.urban' : 'ui.birth.rural')} · {childrenLine(life)}
          <br />
          {t('ui.birth.family', { decile: t(`ui.birth.decile.${life.familyDecile}`) })}
          <br />
          {t(`ui.birth.lifeExp.${life.sex}`, { n: e0, years: yearsWord(e0) })}
        </div>
        <EmptyStrip />
        <div class="actions">
          <button type="button" class="btn primary block" onClick={() => game.startLife()}>
            {t('ui.birth.start')}
          </button>
          {onShare && (
            <button type="button" class="btn block" onClick={() => onShare(life)}>
              {t('ui.birth.share')}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

export function BirthScreen({ game, ds, onShare }: BirthScreenProps) {
  const life = game.life.value;
  const [phase, setPhase] = useState<Phase>(() => (prefersReducedMotion() ? 'card' : 'lottery'));

  useEffect(() => {
    if (phase !== 'wave') return;
    const id = setTimeout(() => setPhase('card'), WAVE_MS);
    return () => clearTimeout(id);
  }, [phase]);

  if (life === null) return null;

  return (
    <main class="page">
      <header class="topbar">
        <b>{t('ui.app.title')}</b>
        <ThemeToggle game={game} />
      </header>
      {phase === 'card' ? (
        <BirthCard game={game} ds={ds} life={life} onShare={onShare} />
      ) : (
        <Lottery ds={ds} life={life} phase={phase} onDraw={() => setPhase('wave')} onSkip={() => setPhase('card')} />
      )}
    </main>
  );
}
